/** 応援の参加の見直し。「一覧 → 確認 → 結果」の順に進める（docs/01 7-9・docs/06 5-2）。 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import { loadFailure, nextStepAfter, type LoadFailure } from '../api/load-failure.js';
import type {
  InactiveSupportList,
  InactiveSupportMember,
  SupportBulkOutcome,
} from '../api/types.js';
import { useSession } from '../auth/session-context.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { DataTable } from '../components/ui/DataTable.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { fullDate } from '../dormancy/labels.js';
import { withReason } from '../format/sentence.js';
import { can } from '../menu/visible.js';

const BULK_MAX = 200;
const keyOf = (row: { channel_id: string; user_id: string }): string =>
  `${row.channel_id}:${row.user_id}`;

interface Banner {
  tone: 'success' | 'error';
  message: string;
  nextStep: ReactNode;
}

export function InactiveSupportPage(): ReactNode {
  const api = useApi();
  const { profile } = useSession();
  const [data, setData] = useState<InactiveSupportList | null>(null);
  const [failure, setFailure] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailure(null);
    api
      .get<InactiveSupportList>('/admin/support/inactive')
      .then((loaded) => {
        if (cancelled) return;
        setData(loaded);
        const selectable = new Set(loaded.items.map(keyOf));
        setSelected((current) => new Set([...current].filter((key) => selectable.has(key))));
      })
      .catch((caught: unknown) => {
        if (!cancelled) setFailure(loadFailure(caught));
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadKey]);

  const chosen = (data?.items ?? []).filter((row) => selected.has(keyOf(row)));
  const people = new Set(chosen.map((row) => row.user_id)).size;
  const selectable = data?.items ?? [];
  const canSettings = profile?.grant ? can(profile.grant, 'settings.manage') : false;

  function toggle(row: InactiveSupportMember, checked: boolean): void {
    setSelected((current) => {
      const next = new Set(current);
      if (checked && next.size < BULK_MAX) next.add(keyOf(row));
      else if (!checked) next.delete(keyOf(row));
      return next;
    });
  }

  async function run(): Promise<void> {
    setBusy(true);
    try {
      const outcome = await api.post<SupportBulkOutcome>('/admin/support/inactive/remove', {
        body: {
          items: chosen.map((row) => ({ channel_id: row.channel_id, user_id: row.user_id })),
        },
      });
      const done = new Set(outcome.done.map((row) => row.user_id)).size;
      const skipped = new Set(outcome.skipped.map((row) => row.user_id)).size;
      const known = new Map(chosen.map((row) => [keyOf(row), row]));
      setResult({
        tone: outcome.skipped.length > 0 && done === 0 ? 'error' : 'success',
        message: [
          ...(outcome.done.length > 0
            ? [`${outcome.done.length}件（${done}名）をトークから外しました。`]
            : []),
          ...(outcome.skipped.length > 0
            ? [`${outcome.skipped.length}件（${skipped}名）は外せませんでした。`]
            : []),
        ].join(''),
        nextStep:
          outcome.skipped.length > 0 ? (
            <>
              外せなかった方と理由です。一覧を開き直して確かめてください。
              <ul className="admin-bulk-skipped">
                {outcome.skipped.map((row) => {
                  const name = known.get(keyOf(row)) ?? row;
                  return (
                    <li key={keyOf(row)}>
                      {name.channel_name}・{name.display_name}さん：{row.reason}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : (
            '外したことは操作履歴に残りました。必要になったら、トークの画面でまた加えられます。'
          ),
      });
      setSelected(new Set());
      setReloadKey((n) => n + 1);
    } catch (caught: unknown) {
      setResult({
        tone: 'error',
        message: withReason(
          'トークから外せませんでした',
          caught instanceof ApiError ? caught.message : messageForStatus(0),
        ),
        nextStep: nextStepAfter(caught, '一覧を開き直して、参加している方を確かめてください。'),
      });
    } finally {
      setConfirming(false);
      setBusy(false);
    }
  }

  return (
    <article>
      <PageHeader
        title="しばらく開いていない応援の方"
        description="応援で加わったトークの参加を見直します。一覧を見ただけでは、どなたも外れません。"
      />
      {result ? <ResultBanner {...result} reveal /> : null}
      {failure ? (
        <ErrorBlock failure={failure} onRetry={() => setReloadKey((n) => n + 1)} />
      ) : !data ? (
        <LoadingBlock />
      ) : (
        <>
          <p className="admin-note">
            {data.inactive_days}
            日より長く、新しいメッセージを開いていない方です。読んでいないメッセージが無い方は出しません。
            外す前に、引き続き必要な方かお確かめください。
            {canSettings ? (
              <>
                日数は<Link to="/settings/dormancy">会社の設定</Link>で変えられます。
              </>
            ) : null}
          </p>
          {data.items.length === 0 ? (
            <EmptyBlock label="いまは、しばらく開いていない応援の方はいません。" />
          ) : (
            <>
              <p>
                対象は{data.people}
                名です。同じ方が複数のトークにいる場合は、トークごとに選びます（全部で{data.total}
                件）。
              </p>
              {data.total > data.items.length ? (
                <p className="admin-note">
                  いまは{data.items.length}件を出しています。外すと、次の分が出ます。
                </p>
              ) : null}
              <div className="admin-bulk-bar" role="group" aria-label="まとめて外す">
                <span className="admin-bulk-count" aria-live="polite">
                  {chosen.length}件（{people}名）を選んでいます。
                </span>
                <Button
                  variant="secondary"
                  compact
                  disabled={busy || selectable.length === 0}
                  onClick={() => setSelected(new Set(selectable.slice(0, BULK_MAX).map(keyOf)))}
                >
                  {selectable.length > BULK_MAX ? '先頭の200件を選ぶ' : '選べる方をすべて選ぶ'}
                </Button>
                <Button
                  variant="quiet"
                  compact
                  disabled={busy || chosen.length === 0}
                  onClick={() => setSelected(new Set())}
                >
                  選ぶのをやめる
                </Button>
                <Button
                  variant="danger"
                  disabled={busy || chosen.length === 0}
                  onClick={() => {
                    setResult(null);
                    setConfirming(true);
                  }}
                >
                  選んだ方をまとめて外す
                </Button>
              </div>
              <p className="admin-note">1回に200件まで選べます。まだトークからは外していません。</p>
              <DataTable label="しばらく開いていない応援の方の一覧" minWidth="850px">
                <thead>
                  <tr>
                    <th scope="col">選ぶ</th>
                    <th scope="col">トーク</th>
                    <th scope="col">お名前</th>
                    <th scope="col">所属拠点</th>
                    <th scope="col">開いていない期間</th>
                    <th scope="col">加わった日</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row) => (
                    <tr key={keyOf(row)}>
                      <td>
                        <label className="ui-check">
                          <input
                            type="checkbox"
                            checked={selected.has(keyOf(row))}
                            disabled={
                              busy || (!selected.has(keyOf(row)) && selected.size >= BULK_MAX)
                            }
                            onChange={(event) => toggle(row, event.target.checked)}
                          />
                          <span className="admin-visually-hidden">
                            {row.channel_name}の{row.display_name}さんを選ぶ
                          </span>
                        </label>
                      </td>
                      <th scope="row">
                        <Link to={`/rooms/${row.channel_id}`}>{row.channel_name}</Link>
                      </th>
                      <td>{row.display_name}さん</td>
                      <td>{row.site_name ?? '拠点なし'}</td>
                      <td>{data.inactive_days}日以上開いていません</td>
                      <td className="ui-cell-date">{fullDate(row.joined_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirming}
        title={`${people}名をトークから外します`}
        message={
          <>
            {chosen
              .slice(0, 10)
              .map((row) => `${row.channel_name}の${row.display_name}さん`)
              .join('、')}
            {chosen.length > 10 ? `、ほか${chosen.length - 10}件` : ''}。選んだ{chosen.length}
            件の参加を外します。外れたトークの新しいメッセージや通知が届かなくなります。
          </>
        }
        reversibility="必要になったら、トークの画面でまた加えられます。外したことは操作履歴に残ります。"
        reversible
        confirmLabel="トークから外す"
        busy={busy}
        onConfirm={() => void run()}
        onCancel={() => setConfirming(false)}
      />
    </article>
  );
}
